// Default components
import {
	CrossStoreCommissions,
	DefaultAdvisors,
	DefaultCommission,
	DefaultFinanceManagerCommission,
	DefaultGrossDepartmental,
	DefaultManagerCommission,
	DefaultPerformanceNormalization,
	MiniDeals,
} from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { getDepartmentalPosition, isMutablePosition, supportedSystemPositions } from "@Common/helpers/Positions";
import { getSanitatedLocationOEMs, getOEMFranchisesLabels } from "@Common/helpers/OEMs";

const POSITIONS = supportedSystemPositions();

const getDepartmentGrossIdentifier = (position) => {
	switch (position) {
		case POSITIONS.SERVICE.SERVICE_MANAGER:
		case POSITIONS.SERVICE.SERVICE_DIRECTOR:
		case POSITIONS.SERVICE.SERVICE_FOREMAN:
			return "service";
		case POSITIONS.PARTS.PARTS_MANAGER:
		case POSITIONS.PARTS.PARTS_DIRECTOR:
			return "parts";
		case POSITIONS.BODY_SHOP.BODY_SHOP_MANAGER:
		case POSITIONS.BODY_SHOP.BODY_SHOP_PRODUCT_MANAGER:
		case POSITIONS.BODY_SHOP.BODY_SHOP_FOREMAN:
			return "bodyShop";
		default:
			break;
	}
	return "";
};

export const getDefaultPaymentType = (position) => {
	switch (position) {
		case POSITIONS.SERVICE.SERVICE_ADVISOR:
			return "Warranty";
		case POSITIONS.SERVICE.INTERNAL_SERVICE_ADVISOR:
			return "Internal";
		default:
			break;
	}
	return "Customer";
};

const getRegularSections = (position, locationOEMs = [], franchises = []) => {
	const spreadFranchises = getOEMFranchisesLabels(locationOEMs);

	const grossIdentifier = getDepartmentGrossIdentifier(position);
	if (grossIdentifier) {
		return {
			[grossIdentifier]: DefaultGrossDepartmental(locationOEMs, grossIdentifier),
		};
	}

	switch (position) {
		case POSITIONS.SALES.SALES_ASSOCIATE:
			return {
				default: DefaultCommission(),
				miniDeal: MiniDeals(spreadFranchises),
			};
		case POSITIONS.FINANCE.FINANCE_MANAGER:
		case POSITIONS.FINANCE.FINANCE_DIRECTOR:
			return {
				default: DefaultFinanceManagerCommission(),
				backendPVRComputation: DefaultPerformanceNormalization(),
			};
		case POSITIONS.SALES.SALES_MANAGER:
		case POSITIONS.SALES.GENERAL_SALES_MANAGER:
			return {
				default: DefaultManagerCommission(franchises),
				crossStoreCommissions: CrossStoreCommissions(),
			};
		case POSITIONS.BODY_SHOP.BODY_SHOP_ESTIMATOR:
		case POSITIONS.SERVICE.SERVICE_ADVISOR:
		case POSITIONS.SERVICE.INTERNAL_SERVICE_ADVISOR: {
			const paymentType = getDefaultPaymentType(position);
			return {
				default: DefaultAdvisors(paymentType),
			};
		}
		default:
			break;
	}

	return null;
};

const getDepartmentalSections = (position, locationOEMs = [], franchises = []) => {
	switch (position) {
		case POSITIONS.FINANCE.FINANCE_DIRECTOR:
			return {
				default: DefaultManagerCommission(franchises),
				crossStoreCommissions: CrossStoreCommissions(),
			};
		case POSITIONS.PARTS.PARTS_COUNTER_ASSOCIATE:
			return {
				parts: DefaultGrossDepartmental(locationOEMs, "parts"),
			};
		default:
			break;
	}
	return null;
};

const getDefaults = (position, positionType, { locationOEMs = [], franchises = [] }) => {
	const isMutable = isMutablePosition(position);
	const isDepartmental = positionType === "Departmental";
	const departmentalPosition = getDepartmentalPosition(position);

	if (isMutable && isDepartmental) {
		return getDepartmentalSections(departmentalPosition, locationOEMs, franchises);
	}

	return getRegularSections(departmentalPosition, locationOEMs, franchises);
};

const getDefaultComponent = (position, positionType, { location, component }) => {
	const locationOEMs = getSanitatedLocationOEMs(location?.oems || []);
	const components = getDefaults(position, positionType, { locationOEMs, franchises: location?.franchises });
	return components?.[component] || null;
};

const getComponents = (position, positionType, { location, components = [] }) => {
	if (!position || !location || !components.length) {
		return null;
	}
	return components?.reduce((accumulator, component) => {
		accumulator[component] = getDefaultComponent(position, positionType, { location, component });
		return accumulator;
	}, {});
};

export const usePayPlansDefaults = (position, positionType, { location, components = [] }) => {
	const result = getComponents(position, positionType, { location, components });
	return { ...result };
};

const getDefaultInformation = (user = null, creation = new Date()) => {
	if (!user) {
		return null;
	}
	const information = {
		_id: user?._id,
		fullName: user?.fullName,
	};
	return {
		createdBy: { ...information },
		updatedBy: { ...information },
		createdAt: creation,
		updatedAt: creation,
	};
};

export const buildPayPlan = (basics, defaults, { user, creation }) => {
	const information = getDefaultInformation(user, creation);
	return {
		...basics,
		...defaults,
		...information,
		locked: false,
	};
};
